import React, { useEffect, useState, useCallback } from 'react';
import { Clock, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../lib/api';

const INTENT_LABEL = {
  defer: 'Follow up later', objection: 'Objection', pricing_request: 'Pricing',
  ready_to_buy: 'Ready to buy', meeting_request: 'Meeting', neutral: 'Replied',
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.round((d - new Date()) / 86400000);
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return days <= 0 ? `${date} (due)` : days === 1 ? `${date} (tomorrow)` : `${date} (in ${days}d)`;
};

// Snoozed leads — deferrals the Brain has parked + when they auto-resume.
export const SnoozedLeadsCard = () => {
  const [leads, setLeads] = useState(null);
  const [open, setOpen] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/brain/snoozed-leads');
      setLeads(res.data?.leads || []);
    } catch (e) { setLeads([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (leads === null) return null;
  if (leads.length === 0) return null; // nothing parked → stay out of the way

  return (
    <div data-testid="snoozed-leads-card" className="rounded-xl border border-border/50 bg-card/40">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5"
        data-testid="snoozed-leads-toggle"
      >
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-indigo-400" />
          <span className="text-sm font-medium text-foreground">Snoozed leads</span>
          <span className="text-[11px] font-semibold text-indigo-400 bg-indigo-500/15 px-1.5 py-0.5 rounded-full" data-testid="snoozed-leads-count">
            {leads.length}
          </span>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="divide-y divide-border/40 border-t border-border/40 max-h-72 overflow-y-auto">
          {leads.map((l) => (
            <a
              key={l.id}
              href={`/leads/${l.id}`}
              className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-muted/40 transition-colors"
              data-testid={`snoozed-lead-${l.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm text-foreground truncate">{l.name || 'Lead'}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {l.company || '—'}{l.last_reply_intent ? ` · ${INTENT_LABEL[l.last_reply_intent] || l.last_reply_intent}` : ''}
                </p>
              </div>
              <span className="text-xs text-indigo-400 shrink-0 font-medium" data-testid={`snoozed-lead-when-${l.id}`}>
                {fmtWhen(l.snooze_until)}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default SnoozedLeadsCard;
